'use client';

import { FC, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { usePathname, useRouter } from 'next/navigation';
import { Linkedin, Instagram, Facebook, Youtube } from 'lucide-react';
import Link from 'next/link';
import { useAppSelector } from '@/hotdesk-ui/hooks/hook';
import { RootState } from '@/hotdesk-ui/store/store';
import { buildLongTermOfficesExploreHref } from '@/hotdesk-ui/helpers/explore-location-params';
import styles from './SiteFooter.module.scss';
export interface SiteFooterProps {
  moveToRoute: (eventName?: string, target?: string) => void;
  appQRCode: () => void;
  osClick: () => void;
  oneClick: () => void;
  hostUrl: string;
  oneURL: string;
  termLink: string;
  privacyPolicyLink: string;
  helpLink: string;
  blogUrl?: string;
}
const SOCIAL_LINKS = [{
  href: 'https://www.linkedin.com/company/hotdeskinc/',
  icon: <Linkedin size={18} />,
  label: 'LinkedIn',
  event: 'linkedin_clicked'
}, {
  href: 'https://www.youtube.com/@hotdesk.global/',
  icon: <Youtube size={18} />,
  label: 'YouTube',
  event: 'youtube_clicked'
}, {
  href: 'https://x.com/hotdeskwrld',
  icon: <svg width="16" height="16" viewBox="10 10 16 16" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M19.3032 16.9278L24.4033 11H23.1946L18.7665 16.1473L15.2298 11H11.1505L16.4985 18.7834L11.1505 25H12.3592L17.0352 19.5645L20.7702 25H24.8495L19.3032 16.9278ZM17.6482 18.8523L17.1063 18.077L12.7943 11.91H14.6505L18.1301 16.887L18.672 17.6623L23.1952 24.1314H21.339L17.6482 18.8523Z" />
        </svg>,
  label: 'X / Twitter',
  event: 'twitter_clicked'
}, {
  href: 'https://www.facebook.com/hotdeskofficial',
  icon: <Facebook size={18} />,
  label: 'Facebook',
  event: 'facebook_clicked'
}, {
  href: 'https://www.instagram.com/hotdesk.io/',
  icon: <Instagram size={18} />,
  label: 'Instagram',
  event: 'instagram_clicked'
}];
const SiteFooter: FC<SiteFooterProps> = ({
  moveToRoute,
  appQRCode,
  osClick,
  oneClick,
  hostUrl,
  oneURL,
  termLink,
  privacyPolicyLink,
  helpLink,
  blogUrl
}) => {
  const t = useTranslations();
  const router = useRouter();
  const pathname = usePathname();
  const listingTypes = useAppSelector((state: RootState) => state.listingTypes.items);
  const workspaceCategories = useAppSelector((state: RootState) => state.workspaceCategories.items);
  const geoLocation = useAppSelector((state: RootState) => state.geoLocation.geoLocationZipData);
  const languageCode = useAppSelector((state: RootState) => state.listing.defaultLanguage);
  // Both lists are populated client-side, so they're empty during SSR.
  // Gate on `mounted` to keep server and first-client renders identical.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  const handleFooterNav = (href: string, eventName?: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (eventName) moveToRoute(eventName);
    const targetPath = href.split('#')[0].split('?')[0];
    if (targetPath === pathname && href.includes('?')) {
      e.preventDefault();
      router.push(href);
      window.scrollTo(0, 0);
    }
  };
  const handleLongTermOffices = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    moveToRoute('long_term_offices_clicked');
    router.push(buildLongTermOfficesExploreHref(geoLocation, languageCode));
  };
  return <footer className={styles.footer} data-sentry-component="SiteFooter" data-sentry-source-file="SiteFooter.tsx">
            <div className={styles.container}>
                <div className={styles.brandRow}>
                    <div className={styles.brandLeft}>
                        <Link href="/" onClick={() => moveToRoute('home_clicked')} className={styles.logoLink} data-sentry-element="Link" data-sentry-source-file="SiteFooter.tsx">
                            <Image src="https://hotdesk-assets.s3.eu-west-1.amazonaws.com/landing_pages/web_v2/home_v4/hotdesk-logo-white.png" alt="Hotdesk Logo" width={56} height={32} unoptimized style={{
              objectFit: 'contain'
            }} data-sentry-element="Image" data-sentry-source-file="SiteFooter.tsx" />
                        </Link>
                        <p className={styles.tagline}>{t('new-landing.footer.tagline')}</p>
                    </div>
                    <div className={styles.brandRight}>
                        <a href="https://apps.apple.com/us/app/hotdesk/id1484609853" className={styles.storeBtn} target="_blank" rel="noopener noreferrer" onClick={appQRCode}>
                            <Image src="https://hotdesk-assets.s3.eu-west-1.amazonaws.com/landing_pages/web_v2/home_v4/apple-icon-white.svg" alt="" width={20} height={24} unoptimized data-sentry-element="Image" data-sentry-source-file="SiteFooter.tsx" />
                            <div className={styles.storeText}>
                                <span className={styles.storeSmall}>Download on the</span>
                                <span className={styles.storeName}>App Store</span>
                            </div>
                        </a>
                        <a href="https://play.google.com/store/apps/details?id=io.hotdesk.app" className={styles.storeBtn} target="_blank" rel="noopener noreferrer" onClick={appQRCode}>
                            <Image src="https://hotdesk-assets.s3.eu-west-1.amazonaws.com/landing_pages/web_v2/home_v4/google-play-icon-white.svg" alt="" width={20} height={22} unoptimized data-sentry-element="Image" data-sentry-source-file="SiteFooter.tsx" />
                            <div className={styles.storeText}>
                                <span className={styles.storeSmall}>GET IT ON</span>
                                <span className={styles.storeName}>Google Play</span>
                            </div>
                        </a>
                    </div>
                </div>

                <div className={styles.linksRow}>
                    <div className={styles.linkColumn}>
                        <h3 className={styles.columnHeading}>{t('footer.links.book')}</h3>
                        <ul className={styles.linkList}>
                            {mounted && listingTypes.map(lt => <li key={lt.id}>
                                    <Link href={`/workspaces?typeIn=${lt.id}`} onClick={handleFooterNav(`/workspaces?typeIn=${lt.id}`, `${(lt.type || '').toLowerCase().replace(/\s+/g, '_')}_clicked`)}>
                                        {lt.type}
                                    </Link>
                                </li>)}
                        </ul>
                    </div>
                    {/* TODO: Add ?category= support to /workspaces page, then update hrefs below */}
                    <div className={styles.linkColumn}>
                        <h3 className={styles.columnHeading}>{t('landing-v4.footer.spaces')}</h3>
                        <ul className={styles.linkList}>
                            {mounted && workspaceCategories.map(wc => <li key={wc.id}>
                                    <Link href="/workspaces" onClick={() => moveToRoute(`${(wc.category || '').toLowerCase().replace(/\s+/g, '_')}_clicked`)}>
                                        {wc.category}
                                    </Link>
                                </li>)}
                            <li><Link href="/workspaces" onClick={() => moveToRoute('all_spaces_clicked')} data-sentry-element="Link" data-sentry-source-file="SiteFooter.tsx">{t('new-landing.footer.all_spaces')}</Link></li>
                        </ul>
                    </div>
                    <div className={styles.linkColumn}>
                        <h3 className={styles.columnHeading}>{t('footer.links.forBusiness')}</h3>
                        <ul className={styles.linkList}>
                            <li>
                                <a href={oneURL} target="_blank" rel="noopener noreferrer" onClick={() => {
                oneClick();
                moveToRoute('hybrid_remote_teams_clicked');
              }}>
                                    {t('nav.hybridRemoteTeams')}
                                </a>
                            </li>
                            <li>
                                <Link href="/contact" onClick={() => moveToRoute('company_setup_clicked')} data-sentry-element="Link" data-sentry-source-file="SiteFooter.tsx">
                                    {t('nav.companySetup')}
                                </Link>
                            </li>
                            <li>
                                <Link href="/workspaces?frequency=month,annual&active=split" onClick={handleLongTermOffices} data-sentry-element="Link" data-sentry-source-file="SiteFooter.tsx">
                                    {t('nav.longTermOffices')}
                                </Link>
                            </li>
                            <li>
                                <Link href="/calculator" onClick={() => moveToRoute('calculator_clicked')} data-sentry-element="Link" data-sentry-source-file="SiteFooter.tsx">
                                    {t('nav.calculator')}
                                </Link>
                            </li>
                        </ul>
                    </div>
                    <div className={styles.linkColumn}>
                        <h3 className={styles.columnHeading}>{t('footer.links.forWorkspaces')}</h3>
                        <ul className={styles.linkList}>
                            <li>
                                <a href={hostUrl} target="_blank" rel="noopener noreferrer" onClick={() => {
                osClick();
                moveToRoute('list_your_workspaces_clicked');
              }}>
                                    {t('footer.links.listYourWorkspaces')}
                                </a>
                            </li>
                        </ul>
                    </div>
                    <div className={styles.linkColumn}>
                        <h3 className={styles.columnHeading}>{t('new-landing.footer.other')}</h3>
                        <ul className={styles.linkList}>
                            <li><a href={`/${blogUrl}`} target="_blank" rel="noopener noreferrer" onClick={() => moveToRoute('blogs_page_clicked')}>{t('footer.links.Blog')}</a></li>
                            <li><Link href="/terms-and-conditions" onClick={() => moveToRoute('terms_and_conditions_clicked')} data-sentry-element="Link" data-sentry-source-file="SiteFooter.tsx">{t('footer.links.terms')}</Link></li>
                            <li><a href={privacyPolicyLink} target="_blank" rel="noopener noreferrer" onClick={() => moveToRoute('privacy_policy_clicked')}>{t('footer.links.privacy')}</a></li>
                            <li><Link href="/ai-info" onClick={() => moveToRoute('ai_info_clicked')} data-sentry-element="Link" data-sentry-source-file="SiteFooter.tsx">{t('footer.links.ai_info')}</Link></li>
                        </ul>
                    </div>
                    <div className={styles.linkColumn}>
                        <h3 className={styles.columnHeading}>{t('footer.links.Support')}</h3>
                        <ul className={styles.linkList}>
                            <li><a href={helpLink} target="_blank" rel="noopener noreferrer" onClick={() => moveToRoute('faq_clicked')}>{t('footer.links.Help')}</a></li>
                            <li><a href='/contact' target="_blank" rel="noopener noreferrer" onClick={() => moveToRoute('contact_us_clicked')}>{t('workspace-details.contactUs')}</a></li>
                            <li><span className={styles.emailText}>{t('footer.links.email')}</span></li>
                        </ul>
                    </div>
                </div>

                <div className={styles.copyrightBar}>
                    <div className={styles.socialIcons}>
                        {SOCIAL_LINKS.map(social => <a key={social.event} href={social.href} target="_blank" rel="noopener noreferrer" className={styles.socialIcon} aria-label={social.label} onClick={() => moveToRoute(social.event)}>
                                {social.icon}
                            </a>)}
                    </div>
                    <p className={styles.copyright}>{t('landing-v4.footer.copyright')}</p>
                </div>
            </div>
        </footer>;
};
export default SiteFooter;